import type { ComponentType } from 'react';
import {
  AgentIcon,
  AutomationIcon,
  CalendarIcon,
  ChatIcon,
  CrmIcon,
  FilesIcon,
  HomeIcon,
  InboxIcon,
  MarketingIcon,
  PipelineIcon,
  SettingsIcon,
  ShieldIcon,
  SuccessIcon,
  WorkIcon,
} from './icons';

/**
 * The navigation, as data.
 *
 * One list drives the rail, the routes and the page titles, so a screen cannot exist without a way
 * to reach it and a menu entry cannot point at nothing. `built` is the honest part: the platform has
 * far more API than it has screens, and an entry that opens an empty page pretending to work is
 * worse than one that says what it is (context.md section 194).
 */

export interface NavChild {
  key: string;
  /** Absolute path. The route table is generated from these. */
  to: string;
  labelKey: string;
  /** False while the screen is still an API without a UI. */
  built: boolean;
}

export interface NavSection {
  key: string;
  labelKey: string;
  icon: ComponentType;
  /** A section with no children is a link; with children it expands. */
  to?: string;
  children?: NavChild[];
  /** Only for Safe Cortex staff. The server decides; this only keeps the rail honest. */
  platformOnly?: boolean;
}

export const NAVIGATION: NavSection[] = [
  { key: 'home', labelKey: 'nav.home', icon: HomeIcon, to: '/' },

  {
    key: 'crm',
    labelKey: 'nav.crm',
    icon: CrmIcon,
    children: [
      { key: 'leads', to: '/crm/leads', labelKey: 'nav.leads', built: true },
      { key: 'contacts', to: '/crm/contatos', labelKey: 'nav.contacts', built: true },
      { key: 'companies', to: '/crm/empresas', labelKey: 'nav.companies', built: true },
      {
        key: 'opportunities',
        to: '/crm/oportunidades',
        labelKey: 'nav.opportunities',
        built: true,
      },
      { key: 'forecast', to: '/crm/previsao', labelKey: 'nav.forecast', built: true },
      { key: 'key-dates', to: '/crm/datas', labelKey: 'nav.keyDates', built: true },
      { key: 'cadences', to: '/crm/cadencias', labelKey: 'nav.cadences', built: true },
      { key: 'orders', to: '/crm/pedidos', labelKey: 'nav.orders', built: true },
      { key: 'import', to: '/crm/importar', labelKey: 'nav.import', built: true },
      { key: 'proposals', to: '/vendas/propostas', labelKey: 'nav.proposals', built: true },
      { key: 'contracts', to: '/vendas/contratos', labelKey: 'nav.contracts', built: true },
    ],
  },

  { key: 'pipeline', labelKey: 'nav.pipeline', icon: PipelineIcon, to: '/funil' },

  {
    key: 'inbox',
    labelKey: 'nav.inbox',
    icon: InboxIcon,
    children: [
      { key: 'conversations', to: '/atendimento', labelKey: 'nav.conversations', built: true },
      { key: 'tickets', to: '/atendimento/chamados', labelKey: 'nav.tickets', built: true },
      { key: 'channels', to: '/atendimento/canais', labelKey: 'nav.channels', built: true },
      { key: 'queues', to: '/atendimento/filas', labelKey: 'nav.queues', built: true },
      { key: 'templates', to: '/atendimento/templates', labelKey: 'nav.templates', built: true },
    ],
  },

  {
    key: 'success',
    labelKey: 'nav.success',
    icon: SuccessIcon,
    children: [
      { key: 'accounts', to: '/sucesso/contas', labelKey: 'nav.accounts', built: true },
      { key: 'onboarding', to: '/sucesso/onboarding', labelKey: 'nav.onboarding', built: true },
      { key: 'playbooks', to: '/sucesso/playbooks', labelKey: 'nav.playbooks', built: true },
    ],
  },

  {
    key: 'marketing',
    labelKey: 'nav.marketing',
    icon: MarketingIcon,
    children: [
      { key: 'forms', to: '/marketing/formularios', labelKey: 'nav.forms', built: true },
      { key: 'pages', to: '/marketing/paginas', labelKey: 'nav.pages', built: true },
      { key: 'domains', to: '/marketing/dominios', labelKey: 'nav.domains', built: true },
      { key: 'email', to: '/marketing/email', labelKey: 'nav.email', built: true },
      // Built, and deliberately not offered yet (Phase 130): the carrier account and the price a
      // customer pays per message are commercial decisions that are not made. The screens stay
      // reachable so the work can be finished; the rail says what it is.
      { key: 'sms', to: '/marketing/sms', labelKey: 'nav.sms', built: false },
      {
        key: 'whatsapp-campaigns',
        to: '/marketing/whatsapp',
        labelKey: 'nav.whatsappCampaigns',
        built: true,
      },
      { key: 'campaigns', to: '/marketing/campanhas', labelKey: 'nav.campaigns', built: true },
      { key: 'social', to: '/marketing/social', labelKey: 'nav.social', built: true },
      { key: 'ads', to: '/marketing/anuncios', labelKey: 'nav.ads', built: true },
    ],
  },

  {
    key: 'work',
    labelKey: 'nav.work',
    icon: WorkIcon,
    children: [
      { key: 'projects', to: '/trabalho/projetos', labelKey: 'nav.projects', built: true },
      { key: 'tasks', to: '/trabalho/tarefas', labelKey: 'nav.tasks', built: true },
      { key: 'backlog', to: '/trabalho/backlog', labelKey: 'nav.backlog', built: true },
    ],
  },

  { key: 'calendar', labelKey: 'nav.calendar', icon: CalendarIcon, to: '/agenda' },
  { key: 'chat', labelKey: 'nav.chat', icon: ChatIcon, to: '/chat' },
  { key: 'automations', labelKey: 'nav.automations', icon: AutomationIcon, to: '/automacoes' },
  {
    key: 'agents',
    labelKey: 'nav.agents',
    icon: AgentIcon,
    children: [
      { key: 'agentList', to: '/agentes', labelKey: 'nav.agentList', built: true },
      { key: 'knowledge', to: '/agentes/conhecimento', labelKey: 'nav.knowledge', built: true },
      { key: 'batches', to: '/agentes/lotes', labelKey: 'nav.batches', built: true },
    ],
  },
  { key: 'files', labelKey: 'nav.files', icon: FilesIcon, to: '/arquivos' },

  {
    key: 'settings',
    labelKey: 'nav.settings',
    icon: SettingsIcon,
    children: [
      { key: 'account', to: '/configuracoes/conta', labelKey: 'nav.account', built: true },
      { key: 'company', to: '/configuracoes/empresa', labelKey: 'nav.company', built: true },
      {
        key: 'preferences',
        to: '/configuracoes/preferencias',
        labelKey: 'nav.preferences',
        built: true,
      },
      {
        key: 'catalogues',
        to: '/configuracoes/cadastros',
        labelKey: 'nav.catalogues',
        built: true,
      },
      { key: 'team', to: '/configuracoes/equipe', labelKey: 'nav.team', built: true },
      { key: 'sso', to: '/configuracoes/sso', labelKey: 'nav.sso', built: true },
      {
        key: 'privacy',
        to: '/configuracoes/privacidade',
        labelKey: 'nav.privacy',
        built: true,
      },
      { key: 'audit', to: '/configuracoes/auditoria', labelKey: 'nav.audit', built: true },
      { key: 'billing', to: '/configuracoes/assinatura', labelKey: 'nav.billing', built: true },
      { key: 'usage', to: '/configuracoes/uso', labelKey: 'nav.usage', built: true },
      {
        key: 'integrations',
        to: '/configuracoes/integracoes',
        labelKey: 'nav.integrations',
        built: true,
      },
    ],
  },

  { key: 'admin', labelKey: 'nav.admin', icon: ShieldIcon, to: '/admin', platformOnly: true },
];

/** Every destination in the rail, flattened — the route table is built from this. */
export function navDestinations(): { to: string; labelKey: string; built: boolean }[] {
  return NAVIGATION.flatMap((section) =>
    section.children
      ? section.children.map((child) => ({
          to: child.to,
          labelKey: child.labelKey,
          built: child.built,
        }))
      : section.to
        ? [{ to: section.to, labelKey: section.labelKey, built: sectionIsBuilt(section.key) }]
        : [],
  );
}

/** Which of the single-link sections have a real screen behind them. */
const BUILT_SECTIONS = new Set([
  'home',
  'pipeline',
  'calendar',
  'chat',
  'automations',
  'agents',
  'files',
  'admin',
]);

/** Whether a section that is a plain link has a screen behind it. */
export function sectionIsBuilt(key: string): boolean {
  return BUILT_SECTIONS.has(key);
}

/** The section a path belongs to, for keeping the rail expanded on the right group. */
export function sectionFor(pathname: string): NavSection | undefined {
  return NAVIGATION.find((section) =>
    section.children
      ? section.children.some((child) => isUnder(pathname, child.to))
      : section.to !== undefined && isUnder(pathname, section.to),
  );
}

/**
 * Which child of a group the current path belongs to — exactly one, or none.
 *
 * `NavLink`'s own matching lights up every prefix, so `/atendimento/canais` lit both "Conversas"
 * (`/atendimento`) and "Canais". Matching by hand, and keeping only the longest match, means a
 * detail page (`/trabalho/tarefas/abc`) still lights its list while a sibling never lights with it.
 */
export function activeChild(section: NavSection, pathname: string): string | null {
  if (!section.children) return null;

  let best: { key: string; length: number } | null = null;

  for (const child of section.children) {
    if (!isUnder(pathname, child.to)) continue;
    if (!best || child.to.length > best.length) best = { key: child.key, length: child.to.length };
  }

  return best?.key ?? null;
}

function isUnder(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';

  return pathname === to || pathname.startsWith(`${to}/`);
}
